import React from 'react';
import { Link } from 'react-router-dom';

/* ─── PALETTE ─────────────────────────────────────────────── */
const V1 = '#7F00FF';
const V2 = '#B163FF';
const V3 = '#51158C';
const V4 = '#2B0057';

/* ─── CATEGORY ICONS ──────────────────────────────────────── */
const CodeIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
  </svg>
);
const BarChartIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);
const PaletteIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="13.5" cy="6.5" r=".5" /><circle cx="17.5" cy="10.5" r=".5" /><circle cx="8.5" cy="7.5" r=".5" />
    <circle cx="6.5" cy="12.5" r=".5" />
    <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
  </svg>
);
const ServerIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="8" rx="2" ry="2" /><rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
    <line x1="6" y1="6" x2="6.01" y2="6" /><line x1="6" y1="18" x2="6.01" y2="18" />
  </svg>
);
const MonitorIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);
const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);
const UsersIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const getCategoryIcon = (category) => {
  if (!category) return <MonitorIcon />;
  const c = category.toLowerCase();
  if (c.includes('web') || c.includes('frontend') || c.includes('react') || c.includes('javascript')) return <CodeIcon />;
  if (c.includes('data') || c.includes('ml') || c.includes('ai') || c.includes('python')) return <BarChartIcon />;
  if (c.includes('design') || c.includes('ui') || c.includes('ux') || c.includes('figma')) return <PaletteIcon />;
  if (c.includes('devops') || c.includes('cloud') || c.includes('docker') || c.includes('server')) return <ServerIcon />;
  return <MonitorIcon />;
};

const levelColor = {
  Beginner:     { text: 'var(--success)',  bg: 'rgba(13,186,121,0.12)',  border: 'rgba(13,186,121,0.25)'  },
  Intermediate: { text: 'var(--warning)',  bg: 'rgba(245,166,35,0.12)',  border: 'rgba(245,166,35,0.25)'  },
  Advanced:     { text: 'var(--danger)',   bg: 'rgba(240,65,85,0.12)',   border: 'rgba(240,65,85,0.25)'   },
};

const CourseCard = ({ course }) => {
  const lvl = levelColor[course.level] || levelColor.Beginner;
  const seatSeed = String(course._id || course.title || '');
  const seatHash = [...seatSeed].reduce((hash, character) => (hash * 31 + character.charCodeAt(0)) >>> 0, 7);
  const totalSeats = Number(course.totalSeats) || 30;
  const displayEnrollmentCount = Math.min(15 + seatHash % 9, totalSeats);

  return (
    <div
      style={{
        background: 'var(--card-solid)',
        border: '1px solid var(--border)',
        borderRadius: 20, overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        transition: 'transform 0.25s, border-color 0.25s, box-shadow 0.25s',
        boxShadow: 'var(--shadow-card)',
        cursor: 'pointer',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-6px)';
        e.currentTarget.style.borderColor = 'var(--border-strong)';
        e.currentTarget.style.boxShadow = 'var(--shadow-hover)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.boxShadow = 'var(--shadow-card)';
      }}
    >
      {/* Card thumbnail */}
      <div style={{
        height: 130,
        background: `linear-gradient(135deg, ${V4}, ${V3})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: V2, position: 'relative', overflow: 'hidden',
      }}>
        {/* Subtle grid pattern */}
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.08,
          backgroundImage: `linear-gradient(${V2} 1px, transparent 1px), linear-gradient(90deg, ${V2} 1px, transparent 1px)`,
          backgroundSize: '24px 24px',
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          {getCategoryIcon(course.category)}
        </div>
        {/* Glow */}
        <div style={{
          position: 'absolute', bottom: -20, right: -20, width: 80, height: 80,
          borderRadius: '50%', background: `radial-gradient(circle, ${V2}44, transparent)`,
          pointerEvents: 'none',
        }} />
      </div>

      {/* Card body */}
      <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 12, flexGrow: 1 }}>
        {/* Badges */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{
            fontSize: 11.5, color: 'var(--badge-pill-text)', fontWeight: 700,
            background: 'var(--badge-pill-bg)', padding: '3px 10px', borderRadius: 999,
            border: '1px solid var(--badge-pill-border)',
          }}>{course.category}</span>
          <span style={{
            fontSize: 11.5, fontWeight: 700,
            color: lvl.text, background: lvl.bg,
            padding: '3px 10px', borderRadius: 999,
            border: `1px solid ${lvl.border}`,
          }}>{course.level}</span>
        </div>

        {/* Title */}
        <h3 style={{
          fontSize: 16, fontWeight: 800, lineHeight: 1.35,
          fontFamily: "'Inter',sans-serif",
          color: 'var(--text)',
        }}>{course.title}</h3>

        {/* Description */}
        <p style={{
          color: 'var(--muted)', fontSize: 13.5, lineHeight: 1.6, flexGrow: 1,
        }}>
          {course.description ? course.description.substring(0, 90) + '...' : ''}
        </p>

        {/* Meta row */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: 'var(--muted)', fontSize: 12.5, paddingTop: 8,
          borderTop: '1px solid var(--border)',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><ClockIcon />{course.duration}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}><UsersIcon />{displayEnrollmentCount}/{course.totalSeats} seats</span>
        </div>

        {/* Price + CTA */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{
            fontFamily: "'Inter',sans-serif", fontWeight: 900,
            fontSize: 24, color: 'var(--text)',
          }}>₹{course.fee.toLocaleString()}</span>
          <Link
            to={`/courses/${course._id}`}
            className="btn btn-primary"
            style={{ padding: '9px 20px', fontSize: 13.5 }}
          >View Details</Link>
        </div>
      </div>
    </div>
  );
};

export default CourseCard;
